/**
 * adultAutismFormKeys.js
 *
 * Single source of truth for Adult Autism core (patient history) form keys
 * on the frontend. Must stay in sync with HISTORY_FORM_KEYS in
 * python-functions/constants/adult_autism/registry.py.
 *
 * Used by the modular patient overview adapter to split history forms from
 * rating scales without duplicating the key list in multiple JS files.
 */

/** Core patient-history forms — always assigned, not toggled via org instrument config. */
export const AUTISM_CORE_FORM_KEYS = new Set([
  'yourInformation',
  'yourCurrentConcerns',
  'yourWellbeing',
  'yourHealth',
  'yourFamily',
  'yourChildhood',
  'yourAdultLife',
  'yourLifestyle',
  'youAndTheLaw',
]);

/** Ordered list for tests and UI that need stable ordering (formNumber 1–9). */
export const ORDERED_AUTISM_HISTORY_FORM_KEYS = [
  'yourInformation',
  'yourCurrentConcerns',
  'yourWellbeing',
  'yourHealth',
  'yourFamily',
  'yourChildhood',
  'yourAdultLife',
  'yourLifestyle',
  'youAndTheLaw',
];

/**
 * Optional history forms — assigned only when the organisation document
 * explicitly opts in via formConfig.adultAutism.historyForms. Unlike core
 * history forms these are org-gated (e.g. The Owl Centre has
 * sensoryExperiences enabled; LMP does not).
 *
 * Must stay in sync with OPTIONAL_HISTORY_FORM_KEYS in
 * python-functions/constants/adult_autism/registry.py.
 */
export const AUTISM_OPTIONAL_HISTORY_FORM_KEYS = new Set(['sensoryExperiences']);

/**
 * Returns true if the given formKey is an Adult Autism history form (core or
 * optional). Use this instead of checking AUTISM_CORE_FORM_KEYS directly
 * when the goal is to distinguish history from rating scales — optional
 * history forms like the Sensory Experiences Questionnaire would otherwise
 * be misclassified as scales.
 */
export function isAutismHistoryForm(formKey) {
  return AUTISM_CORE_FORM_KEYS.has(formKey) || AUTISM_OPTIONAL_HISTORY_FORM_KEYS.has(formKey);
}

/**
 * History forms assigned to Adult Autism informants (7 forms — no rating scales,
 * and no yourInformation / yourCurrentConcerns). Must stay aligned with
 * ORDERED_INFORMANT_HISTORY_FORM_KEYS in
 * python-functions/constants/adult_autism_informant/inclusion.py.
 */
export const AUTISM_INFORMANT_FORM_KEYS = [
  'yourWellbeing',
  'yourHealth',
  'yourFamily',
  'yourChildhood',
  'yourAdultLife',
  'yourLifestyle',
  'youAndTheLaw',
];
